import { useTranslation } from 'react-i18next';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { format, parseISO } from 'date-fns';
import { TrendingUp } from 'lucide-react';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartConfig,
} from '@/components/ui/chart';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { DefectTrendPoint, Granularity } from '../types';
import { EmptyState } from './EmptyState';

// chartConfig label is used by ChartTooltipContent - it will be overridden in formatter
const chartConfig: ChartConfig = {
  defectRate: {
    label: 'Defect Rate',
    color: 'hsl(var(--destructive))',
  },
};

interface DefectTrendChartProps {
  data: DefectTrendPoint[];
  granularity: Granularity;
  isLoading?: boolean;
}

export function DefectTrendChart({ data, granularity, isLoading }: DefectTrendChartProps) {
  const { t } = useTranslation('production');
  // Format x-axis labels based on granularity
  const formatXAxis = (period: string) => {
    const date = parseISO(period);
    switch (granularity) {
      case 'day':
        return format(date, 'MMM d');
      case 'week':
        return format(date, "'W'w MMM");
      case 'month':
        return format(date, 'MMM yyyy');
      default:
        return format(date, 'MMM d');
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('qa.defectRateTrend')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-80 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('qa.defectRateTrend')}</CardTitle>
        </CardHeader>
        <CardContent>
          <EmptyState
            icon={TrendingUp}
            title={t('qa.noDefectDataTitle')}
            description={t('qa.noDefectDataDescription')}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('qa.defectRateTrend')}</CardTitle>
        <CardDescription>
          {t(`qa.defectRateTrendDesc.${granularity}`)}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-80 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis
                dataKey="period"
                tickFormatter={formatXAxis}
                tick={{ fontSize: 12 }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tickFormatter={(value) => `${value}%`}
                domain={[0, 'auto']}
                tick={{ fontSize: 12 }}
                tickLine={false}
                axisLine={false}
              />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    formatter={(value) => [
                      `${Number(value).toFixed(2)}%`,
                      t('qa.defectRateLabel'),
                    ]}
                    labelFormatter={(label) => formatXAxis(label)}
                  />
                }
              />
              <Line
                type="monotone"
                dataKey="defectRate"
                stroke="var(--color-defectRate)"
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
